import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatPrice, errMsg } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { StatusBadge, PaymentBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Trash2, Banknote, LayoutGrid, Columns4, CheckCheck, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["pending", "preparing", "ready", "completed"];

function playChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.18, 0.36].forEach((start, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = [880, 1108, 1318][i];
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + 0.4);
    });
    setTimeout(() => ctx.close(), 1500);
  } catch (e) {}
}
const payBtnClass = (paid) =>
  paid
    ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
    : "border-orange-300 bg-orange-50 text-orange-800 hover:bg-orange-100";
const NEXT = { pending: "preparing", preparing: "ready", ready: "completed" };

export function OrderQueue() {
  const { t, lang } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [view, setView] = useState("status");
  const [sound, setSound] = useState(() => localStorage.getItem("order_sound") !== "off");
  const knownIds = useRef(null);
  const soundRef = useRef(sound);
  soundRef.current = sound;

  const load = () =>
    api.get("/orders").then((r) => {
      const list = r.data;
      if (knownIds.current) {
        const fresh = list.filter((o) => !knownIds.current.has(o.order_id));
        if (fresh.length > 0) {
          toast.info(`${t("new_order_alert")} ${t("table")} ${fresh.map((o) => o.table_number).join(", ")}`);
          if (soundRef.current) playChime();
        }
      }
      knownIds.current = new Set(list.map((o) => o.order_id));
      setOrders(list);
    }).catch(() => {});

  const toggleSound = () => {
    const next = !sound;
    setSound(next);
    localStorage.setItem("order_sound", next ? "on" : "off");
    if (next) playChime();
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, []);

  const patchStatus = async (orderId, status) => {
    try {
      await api.patch(`/orders/${orderId}/status`, { status });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const advance = async (order) => {
    if (!NEXT[order.status]) return;
    await patchStatus(order.order_id, NEXT[order.status]);
    load();
  };

  const remove = async (order) => {
    try {
      await api.delete(`/orders/${order.order_id}`);
      toast.success(t("order_deleted"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const togglePayment = async (order) => {
    try {
      if (order.payment_status === "paid") {
        await api.patch(`/orders/${order.order_id}/payment`, { payment_status: "unpaid" });
        toast.success(t("payment_updated"));
      } else {
        const r = await api.patch(`/tables/${order.table_number}/pay`);
        toast.success(`${t("table")} ${order.table_number}: ${r.data.count} ${t("orders_paid_closed")} · ${formatPrice(r.data.total)}`);
      }
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const unpaidAtTable = (o) => orders.filter((x) => x.table_number === o.table_number && x.payment_status !== "paid");

  const tables = useMemo(() => {
    const map = {};
    for (const o of orders) {
      if (o.status === "completed") continue;
      (map[o.table_number] = map[o.table_number] || []).push(o);
    }
    return Object.entries(map)
      .map(([table, list]) => {
        const items = {};
        let total = 0;
        let people = 0;
        for (const o of list) {
          total += o.total;
          people += o.people || 0;
          for (const i of o.items) {
            const key = lang === "it" ? i.name_it : i.name_en;
            items[key] = (items[key] || 0) + i.quantity;
          }
        }
        return {
          table: Number(table),
          list,
          items,
          total,
          people,
          allPaid: list.every((o) => o.payment_status === "paid"),
        };
      })
      .sort((a, b) => a.table - b.table);
  }, [orders, lang]);

  const advanceAll = async (g) => {
    for (const o of g.list) if (NEXT[o.status]) await patchStatus(o.order_id, NEXT[o.status]);
    load();
  };

  const completeAll = async (g) => {
    for (const o of g.list) await patchStatus(o.order_id, "completed");
    load();
  };

  const paymentAll = async (g) => {
    try {
      if (g.allPaid) {
        for (const o of g.list) await api.patch(`/orders/${o.order_id}/payment`, { payment_status: "unpaid" });
        toast.success(t("payment_updated"));
      } else {
        const r = await api.patch(`/tables/${g.table}/pay`);
        toast.success(`${t("table")} ${g.table}: ${r.data.count} ${t("orders_paid_closed")} · ${formatPrice(r.data.total)}`);
      }
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const peopleCoverLine = (o) =>
    (o.people || 0) > 0 ? (
      <p className="text-xs text-[#786C66] mt-1">
        {o.people} {t("people")} · {t("cover")} {formatPrice(o.cover_total || 0)}
      </p>
    ) : null;

  const viewBtn = (active) =>
    `rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${
      active ? "bg-[#4A2C2A] text-[#FDFBF7] border-[#4A2C2A]" : "bg-white text-[#786C66] border-[#E8DFD5] hover:border-[#D97706]/50"
    }`;

  return (
    <div data-testid="order-queue">
      <div className="flex gap-2 mb-4 items-center">
        <button data-testid="view-by-status" onClick={() => setView("status")} className={viewBtn(view === "status")}>
          <Columns4 className="w-4 h-4 inline mr-1.5 -mt-0.5" />{t("view_by_status")}
        </button>
        <button data-testid="view-by-table" onClick={() => setView("table")} className={viewBtn(view === "table")}>
          <LayoutGrid className="w-4 h-4 inline mr-1.5 -mt-0.5" />{t("view_by_table")}
        </button>
        <button
          data-testid="toggle-sound-btn"
          onClick={toggleSound}
          title={sound ? t("sound_on") : t("sound_off")}
          className={`ml-auto rounded-full p-2 border transition-colors ${sound ? "bg-[#4A2C2A] text-[#FDFBF7] border-[#4A2C2A]" : "bg-white text-[#786C66] border-[#E8DFD5]"}`}
        >
          {sound ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
      </div>

      {view === "table" ? (
        tables.length === 0 ? (
          <p className="text-sm text-[#786C66]" data-testid="no-active-tables">{t("no_active_tables")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {tables.map((g) => (
              <div key={g.table} data-testid={`table-group-${g.table}`} className="bg-white border-2 border-[#4A2C2A]/15 rounded-2xl p-4 shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
                <div className="flex items-center justify-between">
                  <p className="font-heading font-bold text-xl text-[#4A2C2A]">{t("table")} {g.table}</p>
                  <span className="font-heading font-bold text-lg text-[#D97706]">{formatPrice(g.total)}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  {[...new Set(g.list.map((o) => o.status))].map((s) => (
                    <StatusBadge key={s} status={s} />
                  ))}
                  <PaymentBadge paid={g.allPaid} />
                </div>
                {g.people > 0 && (
                  <p className="text-xs text-[#786C66] mt-2">{g.people} {t("people")}</p>
                )}
                <p className="text-sm text-[#1F1615] mt-2" data-testid={`table-group-items-${g.table}`}>
                  {Object.entries(g.items).map(([name, qty]) => `${qty}× ${name}`).join(", ")}
                </p>
                <div className="space-y-2 mt-3">
                  <Button
                    data-testid={`advance-all-${g.table}`}
                    size="sm"
                    onClick={() => advanceAll(g)}
                    className="w-full rounded-full bg-[#4A2C2A] hover:bg-[#36201F] text-[#FDFBF7] text-xs h-8"
                  >
                    {t("advance_all")}
                  </Button>
                  <div className="flex gap-2">
                    <Button
                      data-testid={`payment-all-${g.table}`}
                      size="sm"
                      variant="outline"
                      onClick={() => paymentAll(g)}
                      className={`flex-1 rounded-full text-xs h-8 gap-1 ${payBtnClass(g.allPaid)}`}
                    >
                      <Banknote className="w-3.5 h-3.5" /> {g.allPaid ? t("mark_all_unpaid") : t("mark_all_paid")}
                    </Button>
                    <Button
                      data-testid={`complete-all-${g.table}`}
                      size="sm"
                      variant="outline"
                      onClick={() => completeAll(g)}
                      className="flex-1 rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-xs h-8 gap-1"
                    >
                      <CheckCheck className="w-3.5 h-3.5" /> {t("complete_table")}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          {[...STATUSES, "paid"].map((status) => {
            const list = orders.filter((o) =>
              status === "paid"
                ? o.status === "completed" && o.payment_status === "paid"
                : status === "completed"
                  ? o.status === "completed" && o.payment_status !== "paid"
                  : o.status === status
            );
            return (
              <div key={status} className="bg-[#F6F1E9] border border-[#E8DFD5] rounded-2xl p-3 min-h-[200px]">
                <div className="flex items-center justify-between px-1 mb-3">
                  {status === "paid" ? <PaymentBadge paid full /> : <StatusBadge status={status} />}
                  <span className="text-xs font-bold text-[#786C66]" data-testid={`queue-count-${status}`}>{list.length}</span>
                </div>
                <div className="space-y-2">
                  {list.length === 0 && <p className="text-xs text-[#786C66] px-1">{t("empty_queue")}</p>}
                  {list.map((o) => (
                    <div key={o.order_id} data-testid={`queue-order-${o.order_id}`} className="bg-white border border-[#E8DFD5] rounded-xl p-3 shadow-sm">
                      <div className="flex items-center justify-between">
                        <p className="font-bold text-sm text-[#1F1615]">{t("table")} {o.table_number}</p>
                        <span className="font-heading font-bold text-[#D97706] text-sm">{formatPrice(o.total)}</span>
                      </div>
                      <div className="mt-1.5">
                        <PaymentBadge paid={o.payment_status === "paid"} />
                      </div>
                      <p className="text-xs text-[#786C66] mt-0.5">{t("order_from")} {o.customer_name}</p>
                      <p className="text-sm text-[#1F1615] mt-1.5">
                        {o.items.map((i) => `${i.quantity}× ${lang === "it" ? i.name_it : i.name_en}`).join(", ")}
                      </p>
                      {peopleCoverLine(o)}
                      {o.note && <p className="text-xs italic text-[#786C66] mt-1">“{o.note}”</p>}
                      {NEXT[o.status] && (
                        <Button
                          data-testid={`advance-order-${o.order_id}`}
                          size="sm"
                          onClick={() => advance(o)}
                          className="mt-2 w-full rounded-full bg-[#4A2C2A] hover:bg-[#36201F] text-[#FDFBF7] text-xs h-8"
                        >
                          {t("move_to")}: {t(`status_${NEXT[o.status]}`)}
                        </Button>
                      )}
                      {o.status === "completed" && (
                        <Button
                          data-testid={`delete-order-${o.order_id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => remove(o)}
                          className="mt-2 w-full rounded-full border-red-200 text-red-600 hover:bg-red-50 text-xs h-8 gap-1"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> {t("delete_order")}
                        </Button>
                      )}
                      <Button
                        data-testid={`toggle-payment-${o.order_id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => togglePayment(o)}
                        className={`mt-2 w-full rounded-full text-xs h-8 gap-1 ${payBtnClass(o.payment_status === "paid")}`}
                      >
                        <Banknote className="w-3.5 h-3.5" /> {o.payment_status === "paid" ? t("mark_unpaid") : t("mark_paid")}
                        {o.payment_status !== "paid" && unpaidAtTable(o).length > 1 && (
                          <span data-testid={`table-bill-${o.order_id}`} className="ml-1 font-normal opacity-80">
                            · {unpaidAtTable(o).length} {t("orders_short")} {formatPrice(unpaidAtTable(o).reduce((s, x) => s + x.total, 0))}
                          </span>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
